import { IconButton, Stack, Typography } from '@mui/material';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import { useTranslation } from 'react-i18next';

interface StationPageHeaderProps {
  address?: string;
  /**
   * Go back to the connector list. Omitted when there is nothing to go back to
   * (single-connector station, or a charge already running on this connector),
   * and the row then carries the address alone.
   */
  onBack?: () => void;
  /**
   * Re-read station and session state from REST. The screen is driven by a
   * WebSocket that can drop or replay stale frames, so the driver needs a way
   * to ask for the truth — most of all on "insert the connector", where a
   * missed `Preparing` leaves them looking at a screen that never moves.
   */
  onRefresh?: () => void;
  /** Spins the refresh control and blocks a second tap while in flight. */
  refreshing?: boolean;
}

/** Matches the back arrow's tap target so the two read as a pair. */
const TAP_TARGET = 44;

/**
 * The station screen's title row: back arrow, street address and refresh on
 * ONE line.
 *
 * These used to be two stacked rows — a small centred address, then a
 * left-aligned "Change connector" text button underneath — which read as two
 * unrelated headers, split the eye between two alignments and pushed the
 * connector card further down a screen that already scrolls on short phones.
 */
export const StationPageHeader: React.FC<StationPageHeaderProps> = ({
  address,
  onBack,
  onRefresh,
  refreshing = false,
}) => {
  const { t } = useTranslation();

  if (!address && !onBack && !onRefresh) return null;

  return (
    <Stack
      direction="row"
      alignItems="center"
      sx={{ width: '100%', gap: '4px', minHeight: TAP_TARGET }}
    >
      {onBack && (
        <IconButton
          onClick={onBack}
          aria-label={t('chargeStationPage.changeConnector')}
          sx={{
            color: 'common.white',
            width: TAP_TARGET,
            height: TAP_TARGET,
            // Optically align the glyph with the content edge while keeping the
            // full 44px tap target.
            marginLeft: '-10px',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <ArrowBackIosNewRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      {address && (
        <Typography
          variant="body1"
          color="common.white"
          sx={{
            fontWeight: 600,
            minWidth: 0,
            flex: 1,
            // Long addresses wrap to a second line rather than being cut off —
            // the address is how the driver confirms they are at the right
            // station — but never past two, so the title cannot grow into the
            // stack of rows this replaced.
            display: '-webkit-box',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            overflow: 'hidden',
          }}
        >
          {address}
        </Typography>
      )}
      {onRefresh && (
        <IconButton
          onClick={onRefresh}
          disabled={refreshing}
          aria-label={t('chargeStationPage.refresh')}
          sx={{
            color: 'common.white',
            width: TAP_TARGET,
            height: TAP_TARGET,
            // Mirrors the back arrow's optical inset against the other edge.
            marginRight: '-10px',
            // Pushed to the end even when there is no address to fill the gap.
            marginLeft: 'auto',
            flexShrink: 0,
            WebkitTapHighlightColor: 'transparent',
            // Disabled here means "already working", not "unavailable" — keep
            // it legible rather than letting MUI grey it out.
            '&.Mui-disabled': { color: 'common.white', opacity: 0.55 },
            '& svg': {
              fontSize: 22,
              animation: refreshing ? 'stationRefreshSpin 900ms linear infinite' : 'none',
            },
            '@keyframes stationRefreshSpin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              '& svg': { animation: 'none' },
            },
          }}
        >
          <RefreshRoundedIcon />
        </IconButton>
      )}
    </Stack>
  );
};
